import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, 
  ChevronLeft,
  Trophy,
  Users,
  Wallet,
  TrendingUp,
  Calendar,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { formatCurrency, formatDateTime, getEloRank } from '../lib/utils';

export default function AdminReports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, reportsRes, txRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/reports'),
        api.get('/admin/transactions?limit=50'),
      ]);
      setStats(statsRes.data);
      setReports(reportsRes.data);
      setTransactions(txRes.data || []);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'topup':
        return <ArrowDownRight className="w-4 h-4 text-green-500" />;
      case 'tournament_fee':
      case 'league_fee':
        return <Trophy className="w-4 h-4 text-amber-500" />;
      default:
        return <ArrowUpRight className="w-4 h-4 text-red-500" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-64 mb-8" />
          <Skeleton className="h-96 rounded-2xl" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-reports">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-display font-bold text-2xl text-tunis-blue">
              Reports & Statistiken
            </h1>
            <p className="text-muted-foreground">Detaillierte Plattform-Analyse</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
            <CardContent className="p-4">
              <Users className="w-8 h-8 mb-2 opacity-80" />
              <div className="text-3xl font-bold">{stats?.total_users || 0}</div>
              <div className="text-white/80">Benutzer gesamt</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
            <CardContent className="p-4">
              <Wallet className="w-8 h-8 mb-2 opacity-80" />
              <div className="text-3xl font-bold">{formatCurrency(stats?.total_topups || 0)}</div>
              <div className="text-white/80">Aufladungen</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0">
            <CardContent className="p-4">
              <Trophy className="w-8 h-8 mb-2 opacity-80" />
              <div className="text-3xl font-bold">{formatCurrency(stats?.total_fees || 0)}</div>
              <div className="text-white/80">Gebühren</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
            <CardContent className="p-4">
              <Calendar className="w-8 h-8 mb-2 opacity-80" />
              <div className="text-3xl font-bold">{stats?.completed_matches || 0}</div>
              <div className="text-white/80">Spiele beendet</div>
            </CardContent>
          </Card>
        </div>

        {/* Weekly Growth */}
        <Card className="border-slate-200 mb-8">
          <CardHeader>
            <CardTitle className="text-tunis-blue flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Wachstum diese Woche
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <div className="text-4xl font-bold text-tunis-blue">+{reports?.weekly_stats?.new_users || 0}</div>
                <div className="text-muted-foreground">Neue Benutzer</div>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <div className="text-4xl font-bold text-tunis-blue">+{reports?.weekly_stats?.new_matches || 0}</div>
                <div className="text-muted-foreground">Neue Spiele</div>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <div className="text-4xl font-bold text-tunis-blue">+{reports?.weekly_stats?.new_teams || 0}</div>
                <div className="text-muted-foreground">Neue Teams</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="transactions">
          <TabsList className="mb-6">
            <TabsTrigger value="transactions">
              <Wallet className="w-4 h-4 mr-2" />
              Transaktionen
            </TabsTrigger>
            <TabsTrigger value="players">
              <Users className="w-4 h-4 mr-2" />
              Top Spieler
            </TabsTrigger>
            <TabsTrigger value="teams">
              <Trophy className="w-4 h-4 mr-2" />
              Top Teams
            </TabsTrigger>
          </TabsList>

          {/* Transactions */}
          <TabsContent value="transactions">
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {transactions.map((tx) => (
                    <div key={tx.id} className="flex items-center gap-4 p-4 hover:bg-slate-50">
                      <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                        {getTransactionIcon(tx.type)}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-tunis-blue">{tx.description}</div>
                        <div className="text-sm text-muted-foreground">
                          {tx.type} • {formatDateTime(tx.created_at)}
                        </div>
                      </div>
                      <div className={`font-bold ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Top Players */}
          <TabsContent value="players">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="space-y-3">
                  {reports?.top_players?.map((player, idx) => {
                    const eloRank = getEloRank(player.elo_points);
                    return (
                      <div key={player.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                          idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-600' : 'bg-tunis-blue'
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-tunis-blue">{player.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {player.matches_played} Spiele • {player.wins} Siege
                          </div>
                        </div>
                        <Badge className={`${eloRank.bg} ${eloRank.color}`}>
                          {player.elo_points} ELO
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Top Teams */}
          <TabsContent value="teams">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="space-y-3">
                  {reports?.top_teams?.map((team, idx) => (
                    <div key={team.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                        idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-600' : 'bg-tunis-teal'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-tunis-blue">{team.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {team.member_count} Mitglieder • {team.wins} Siege
                        </div>
                      </div>
                      <Badge className="bg-tunis-teal/10 text-tunis-teal">
                        {team.team_elo} ELO
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
